import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, TextField, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import autocompleteConfig from '../../utils/autocomplete_config';
import {buildRootPatternSource, getPromptParameterPattern, getPromptSegments, getRecognizedInterpolationRanges} from '../../utils/utils';

const SkillsNewPrompt = (props) => {
  const {prompt, setParameters, setPrompt} = props;
  const [activeAutocomplete, setActiveAutocomplete] = React.useState(null);
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);

  const inputRef = React.useRef(null);
  const variableExpressionPattern = /^[A-Za-z0-9_.-]*$/;

  const promptDefinitions = autocompleteConfig;
  const rootNames = Object.keys(promptDefinitions);
  const rootNameLookup = rootNames.reduce((lookup, rootName) => {
    lookup[rootName.toLowerCase()] = rootName;
    return lookup;
  }, {});
  const rootPatternSource = buildRootPatternSource(rootNames);
  const autoParameterNamePattern = new RegExp(`^(?:${rootPatternSource})[A-Za-z0-9_-]*$`, 'i');

  const findOpenExpressionIndex = (beforeCaret) => {
    let searchIndex = beforeCaret.length;

    while (searchIndex > 0) {
      const triggerIndex = beforeCaret.lastIndexOf('{{', searchIndex - 1);

      if (triggerIndex === -1) {
        return -1;
      }

      if (beforeCaret.indexOf('}}', triggerIndex + 2) === -1) {
        return triggerIndex;
      }

      searchIndex = triggerIndex;
    }

    return -1;
  };

  const getReplacementEnd = (value, replacementStart) => {
    let replacementEnd = replacementStart;
    while (replacementEnd < value.length && /[A-Za-z0-9_-]/.test(value[replacementEnd])) {
      replacementEnd += 1;
    }
    return replacementEnd;
  };

  const syncPromptParameters = React.useCallback((nextPrompt) => {
    const promptParameterPattern = getPromptParameterPattern(rootPatternSource);
    const promptParameters = [];
    const seenNames = new Set();

    Array.from(nextPrompt.matchAll(promptParameterPattern)).forEach((match) => {
      const name = match[1];
      const rootName = rootNameLookup[match[2].toLowerCase()];
      const type = promptDefinitions[rootName].type;

      if (seenNames.has(name)) {
        return;
      }

      seenNames.add(name);
      promptParameters.push({
        name,
        type,
      });
    });

    setParameters((currentParameters) => {
      const nextParameters = [
        ...currentParameters.filter((parameter) => !autoParameterNamePattern.test(parameter?.name || '')),
        ...promptParameters,
      ];

      if (currentParameters.length !== nextParameters.length) {
        return nextParameters;
      }

      const hasChanges = currentParameters.some((parameter, index) => {
        return parameter?.name !== nextParameters[index]?.name ||
          parameter?.type !== nextParameters[index]?.type;
      });

      return hasChanges ? nextParameters : currentParameters;
    });
  }, [autoParameterNamePattern, promptDefinitions, rootNameLookup, rootPatternSource, setParameters]);

  const resolveActiveAutocomplete = (params) => {
    const {value, caretPosition} = params;

    const caret = typeof caretPosition === 'number' ? caretPosition : value.length;
    const beforeCaret = value.slice(0, caret);
    const triggerIndex = findOpenExpressionIndex(beforeCaret);

    if (triggerIndex === -1) {
      return null;
    }

    const rawExpression = beforeCaret.slice(triggerIndex + 2);
    const leadingWhitespaceLength = rawExpression.match(/^\s*/)?.[0]?.length || 0;
    const expressionValue = rawExpression.slice(leadingWhitespaceLength);

    if (
      /[\r\n{}]/.test(expressionValue) ||
      !variableExpressionPattern.test(expressionValue)
    ) {
      return null;
    }

    if ((expressionValue.match(/\./g) || []).length > 1) {
      return null;
    }

    const [rootQuery = '', fieldQuery] = expressionValue.split('.');
    const isFieldAutocomplete = typeof fieldQuery === 'string';
    const query = isFieldAutocomplete ? fieldQuery : rootQuery;

    const start = triggerIndex +
      2 +
      leadingWhitespaceLength +
      (isFieldAutocomplete ? rootQuery.length + 1 : 0);
    const end = getReplacementEnd(value, start);

    if (!isFieldAutocomplete) {
      return {
        level: 'root',
        query: rootQuery.toLowerCase(),
        start,
        end,
      };
    }

    const rootName = rootNameLookup[rootQuery.toLowerCase()];

    if (!rootName) {
      return null;
    }

    return {
      level: 'field',
      query: query.toLowerCase(),
      rootName,
      start,
      end,
    };
  };

  const applyAutocompleteOption = ({
    sourceValue,
    activeAutocomplete,
    option,
  }) => {
    const beforeAutocomplete = sourceValue.slice(0, activeAutocomplete.start);
    const afterAutocomplete = sourceValue.slice(activeAutocomplete.end);
    const shouldKeepOpen = activeAutocomplete.level === 'root';
    const suffix = shouldKeepOpen ?
      (afterAutocomplete.startsWith('.') ? '' : '.') :
      (afterAutocomplete.startsWith('}}') ? '' : '}}');
    const nextValue = `${beforeAutocomplete}${option}${suffix}${afterAutocomplete}`;
    const nextCaret = beforeAutocomplete.length + option.length + (shouldKeepOpen ? 1 : 2);

    return {
      nextValue,
      nextCaret,
      shouldKeepOpen,
    };
  };

  const closeAutocompleteMenu = () => {
    setActiveAutocomplete(null);
    setHighlightedOptionIndex(0);
  };

  const handleCursorUpdate = (cursorState) => {
    const nextAutocomplete = resolveActiveAutocomplete({
      value: cursorState.value,
      caretPosition: cursorState.caret,
    });

    if (!nextAutocomplete) {
      closeAutocompleteMenu();
      return;
    }

    setActiveAutocomplete(nextAutocomplete);
  };

  const autocompleteOptions = React.useMemo(() => {
    if (!activeAutocomplete) {
      return [];
    }

    const optionSource = activeAutocomplete.level === 'root' ?
      rootNames :
      (promptDefinitions[activeAutocomplete.rootName]?.fields || []);

    return optionSource.filter((option) => {
      return !activeAutocomplete.query || option.toLowerCase().includes(activeAutocomplete.query);
    });
  }, [activeAutocomplete, promptDefinitions, rootNames]);

  React.useEffect(() => {
    syncPromptParameters(prompt);
  }, [prompt, syncPromptParameters]);

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [activeAutocomplete?.level, activeAutocomplete?.query, activeAutocomplete?.rootName]);

  const handlePromptChange = (nextPrompt) => {
    setPrompt(nextPrompt);
    handleCursorUpdate({
      value: nextPrompt,
      caret: inputRef.current?.selectionStart,
    });
  };

  const handlePromptCursorEvent = () => {
    if (!inputRef.current) {
      return;
    }

    handleCursorUpdate({
      value: inputRef.current.value,
      caret: inputRef.current.selectionStart,
    });
  };

  const handleApplyAutocomplete = (option) => {
    if (!activeAutocomplete || !option) {
      return;
    }

    const {nextValue, nextCaret, shouldKeepOpen} = applyAutocompleteOption({
      sourceValue: prompt,
      activeAutocomplete,
      option,
    });

    setPrompt(nextValue);

    if (shouldKeepOpen) {
      handleCursorUpdate({
        value: nextValue,
        caret: nextCaret,
      });
    } else {
      closeAutocompleteMenu();
    }

    requestAnimationFrame(() => {
      inputRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handlePromptKeyDown = (event) => {
    if (!activeAutocomplete) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeAutocompleteMenu();
      return;
    }

    if (autocompleteOptions.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedOptionIndex((currentIndex) => (currentIndex + 1) % autocompleteOptions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedOptionIndex((currentIndex) => (currentIndex - 1 + autocompleteOptions.length) % autocompleteOptions.length);
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      handleApplyAutocomplete(autocompleteOptions[highlightedOptionIndex] || autocompleteOptions[0]);
    }
  };
  const promptSegments = React.useMemo(() => {
    const ranges = getRecognizedInterpolationRanges(prompt, rootPatternSource);
    return getPromptSegments(prompt, ranges);
  }, [prompt, rootPatternSource]);

  const renderAutocompleteMenu = (open) => {
    if (!open) {
      return null;
    }

    return (
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 'calc(100% + 6px)',
          maxHeight: 220,
          overflowY: 'auto',
          border: '1px solid #E8E4DE',
          borderRadius: '8px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 8px 20px rgba(35, 32, 28, 0.12)',
          zIndex: 8,
          p: 0.5,
        }}
      >
        {
          autocompleteOptions.map((option, optionIndex) => (
            <Box
              key={option}
              component='button'
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setHighlightedOptionIndex(optionIndex)}
              onClick={() => handleApplyAutocomplete(option)}
              sx={{
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: optionIndex === highlightedOptionIndex ? '#F7F4EF' : 'transparent',
                px: 1,
                py: 0.75,
                cursor: 'pointer',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#2D2926',
                  lineHeight: 1.3,
                }}
              >
                {option}
              </Typography>
            </Box>
          ))
        }
        {
          autocompleteOptions.length === 0 &&
          <Typography
            variant='body2'
            sx={{
              px: 1,
              py: 0.75,
              color: '#8E887F',
            }}
          >
            {translate('tasks.Common.TaskEditor.NoMatchesFound')}
          </Typography>
        }
      </Box>
    );
  };

  return (
    <Stack sx={{gap: '8px'}}>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#A8A49E',
          lineHeight: '100%',
          textTransform: 'uppercase',
        }}
      >
        <Translate expression='skills.skills.New.InstructionsLabel' />
      </Typography>
      <Box sx={{position: 'relative', width: '100%'}}>
        {
          prompt &&
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                px: '14px',
                py: '16.5px',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '150%',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                color: '#1A1916',
                boxSizing: 'border-box',
              }}
            >
              {
                promptSegments.map((segment) => (
                  <Box
                    key={segment.key}
                    component='span'
                    sx={{
                      color: segment.highlighted ? '#2B63D9' : 'inherit',
                    }}
                  >
                    {segment.text}
                  </Box>
                ))
              }
            </Box>
          </Box>
        }
        <TextField
          fullWidth
          value={prompt}
          onChange={handlePromptChange}
          placeholder={translate('skills.skills.New.InstructionsPlaceholder')}
          multiline
          minRows={1}
          onKeyDown={handlePromptKeyDown}
          onKeyUp={handlePromptCursorEvent}
          onClick={handlePromptCursorEvent}
          onFocus={handlePromptCursorEvent}
          onBlur={closeAutocompleteMenu}
          inputRef={inputRef}
          sx={{
            '& .MuiOutlinedInput-root': {
              'borderRadius': '12px',
              'backgroundColor': '#FFFFFF',
              'alignItems': 'flex-start',
              'padding': '16.5px 14px',
            },
            '& .MuiOutlinedInput-input': {
              'position': 'relative',
              'zIndex': 2,
              'padding': 0,
              'fontSize': '14px',
              'fontWeight': 400,
              'lineHeight': '150%',
              'whiteSpace': 'pre-wrap',
              'wordBreak': 'break-word',
              'overflowWrap': 'anywhere',
              'color': prompt ? 'transparent' : '#1A1916',
              'WebkitTextFillColor': prompt ? 'transparent' : 'unset',
              'caretColor': '#1A1916',
              'boxSizing': 'border-box',
            },
            '& .MuiOutlinedInput-inputMultiline': {
              'padding': 0,
              'resize': 'none',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: '#A8A49E',
              opacity: 1,
              whiteSpace: 'pre-line',
            },
          }}
        />
        {renderAutocompleteMenu(Boolean(activeAutocomplete))}
      </Box>
    </Stack>
  );
};

SkillsNewPrompt.propTypes = {
  prompt: PropTypes.string.isRequired,
  setParameters: PropTypes.func.isRequired,
  setPrompt: PropTypes.func.isRequired,
};

export default React.memo(SkillsNewPrompt);
